import React, { useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { previewMonthlyEmails, sendMonthlyEmails, sendMonthlyTestEmail } from "../lib/api";

/* Monthly report emails, for admins.

   Each technologist with QA'd cases in the month gets a short email linking to
   their own My Monthly Report page - no scores in the email itself. The
   automatic send on the 1st is off until MONTHLY_REPORT_EMAIL_ENABLED is set,
   so this panel is how a month actually goes out.

   Check first, then send: the check sends nothing and shows who would be
   emailed and who has no address on file. A real send skips anyone already
   emailed for that month, so clicking twice can't email anyone twice. */

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function lastMonths(n) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    d.setMonth(d.getMonth() - 1);
    out.push(monthKey(d));
  }
  return out;
}

export default function MonthlyEmailPanel() {
  const { accounts } = useMsal();
  const myEmail = accounts?.[0]?.username || "";
  const months = useMemo(() => lastMonths(12), []);
  const [month, setMonth]     = useState(months[0]);
  const [testTo, setTestTo]   = useState(myEmail);
  const [busy, setBusy]       = useState(null);     // "check" | "test" | "send"
  const [result, setResult]   = useState(null);     // last check or send summary
  const [error, setError]     = useState(null);
  const [note, setNote]       = useState(null);

  const run = async (kind, fn) => {
    setBusy(kind); setError(null); setNote(null);
    try {
      const r = await fn();
      if (kind === "test") setNote(`Test email sent to ${r.sent_to || testTo}.`);
      else setResult({ ...r, kind });
    } catch (e) {
      setError(e?.message || String(e));
    }
    setBusy(null);
  };

  const check = () => run("check", () => previewMonthlyEmails(month));

  const sendTest = () => {
    const to = testTo.trim();
    if (!to) return;
    run("test", () => sendMonthlyTestEmail(month, to));
  };

  const send = () => {
    const n = result && result.kind === "check" ? result.would_send : null;
    const who = n == null ? "every technologist with cases" : `${n} technologist${n === 1 ? "" : "s"}`;
    if (!window.confirm(
      `Email ${who} their ${monthLabel(month)} report?\n\n` +
      `Each one gets a link to their own report. Anyone already emailed for ` +
      `${monthLabel(month)} is skipped. This can't be undone.`)) return;
    run("send", () => sendMonthlyEmails(month));
  };

  const sent = result && result.kind === "send";
  const count = result ? (sent ? result.sent : result.would_send) : null;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <div>
          <div className="card-title">Monthly report emails</div>
          <div className="card-subtitle">
            Each technologist gets a link to their own report for the month. Scores stay in the portal.
          </div>
        </div>
      </div>
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", marginBottom: 4 }}>Month</div>
            <select value={month} onChange={e => { setMonth(e.target.value); setResult(null); setNote(null); }}
              style={{ fontSize: 13, padding: "5px 9px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--white)" }}>
              {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={check} disabled={!!busy}>
            {busy === "check" ? "Checking…" : "Check who gets an email"}
          </button>
          <div style={{ flex: "1 1 200px" }}>
            <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", marginBottom: 4 }}>Send a test to</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@example.com"
                style={{ flex: 1, minWidth: 160, fontSize: 12.5, padding: "5px 9px", borderRadius: 6, border: "1px solid var(--border)" }}/>
              <button className="btn btn-secondary btn-sm" onClick={sendTest} disabled={!!busy || !testTo.trim()}>
                {busy === "test" ? "Sending…" : "Send test"}
              </button>
            </div>
          </div>
        </div>

        {note && (
          <div style={{ fontSize: 12.5, color: "#0f766e", background: "#f0fdfa", border: "1px solid #ccfbf1",
            borderRadius: 6, padding: "8px 12px" }}>{note}</div>
        )}

        {error && (
          <div style={{ fontSize: 12.5, color: "var(--error)", background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.25)", borderRadius: 6, padding: "8px 12px" }}>{error}</div>
        )}

        {result && (
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", background: "var(--gray-50, #f9fafb)",
            border: "1px solid var(--border)", borderRadius: 6, padding: "10px 12px", lineHeight: 1.7 }}>
            <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
              {sent ? `Sent ${result.sent} email${result.sent === 1 ? "" : "s"} for ${monthLabel(month)}.`
                    : `${count} technologist${count === 1 ? "" : "s"} would be emailed for ${monthLabel(month)}.`}
            </div>
            <div>{result.techs} technologist{result.techs === 1 ? "" : "s"} had cases that month.</div>
            {(result.recipients || []).length > 0 && (
              <details style={{ marginTop: 2 }}>
                <summary style={{ cursor: "pointer" }}>
                  {sent ? "Who was emailed" : "Who would be emailed"} ({result.recipients.length})
                </summary>
                <div style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "4px 0 0 14px" }}>
                  {result.recipients.map(r => r.name || r).join(", ")}
                </div>
              </details>
            )}
            {(result.skipped || []).length > 0 && (
              <div>
                {result.skipped.length} switched off on the Users page, so they won't get one:{" "}
                {result.skipped.map(r => r.name || r).join(", ")}.
              </div>
            )}
            {result.already_sent > 0 && (
              <div>
                {result.already_sent} already emailed for this month, and will be skipped
                {(result.already_sent_techs || []).length > 0 ? `: ${result.already_sent_techs.join(", ")}` : ""}.
              </div>
            )}
            {result.failed > 0 && (
              <div style={{ color: "var(--error)" }}>
                {result.failed} failed: {(result.failed_techs || []).join(", ")}. Try again to retry just those.
              </div>
            )}
            {(result.no_email || []).length > 0 && (
              <div style={{ color: "#92400e" }}>
                No email address on file, so they won't get one: {result.no_email.join(", ")}.
                Add their address on the Users page.
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={send} disabled={!!busy || count === 0}>
            {busy === "send" ? "Sending…" : `Send ${monthLabel(month)} emails`}
          </button>
          <span style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>
            Check first. Anyone already emailed for the month is skipped, so a second send can't double up.
          </span>
        </div>
      </div>
    </div>
  );
}
